import React, { useCallback, useEffect, useRef, useState } from 'react';
import capitalize from 'lodash/capitalize';
import tw from 'tailwind-styled-components';
import PropTypes from "prop-types";
import { Alert, Button, ButtonGroup, ButtonGroupItem, Dropdown } from '@boozallen-uip/uswds-react-core';
import { ACCESS_LEVEL_ADMIN, ACCESS_LEVEL_USER } from '../../utlities/core.constants';
import useAccount from '../../hooks/useAccount.hook';
import { formatShortDisplayDate } from '../../utlities/common.funcs';
import { displayAccessLevelRoleName } from './accessLevelDropdownOptions';
import AppModal from '../../components/modals/AppModal';
import denialReasonOptions from './denialReasonOptions';
import AccountChangeNotice from './AccountChangeNotice.component';

const UserRequestContainer = tw.div`grid grid-cols-12 items-start border-2 border-gray-lighter mb-4 rounded-md py-2 px-4`;

export default function ApprovedUserRequest({
  request,
  onChange,
  onAccessLevelSaved,
  onUndoAccessLevel,
  onEdit,
  recentChange,
  shouldFocusAccessLevel,
  accessLevelOptions,
}) {
  const accessLevelSelectRef = useRef(null);
  const [accessLevel, setAccessLevel] = useState(request.accessLevel);
  const [isSavingAccessLevel, setIsSavingAccessLevel] = useState(false);
  const [hasSaveError, setHasSaveError] = useState(false);
  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [isSubmittingRestore, setIsSubmittingRestore] = useState(false);
  const [hasRestoreError, setHasRestoreError] = useState(false);
  const [revokeReason, setRevokeReason] = useState(null);
  const [revokeReasonDropdownKey, setRevokeReasonDropdownKey] = useState(0);
  const { approveUserAccountRequest, denyUserAccountRequest } = useAccount();
  const accessLevelHelpId = `access-level-help-${request.id}`;
  const unsavedHintId = `access-level-unsaved-${request.id}`;
  const actionHelpId = `account-action-help-${request.id}`;
  const restoreModalId = `restore-account-modal-${request.id}`;
  const isInactiveAccount = request.accountStatus === "Inactive";
  const isRevokedAccount = request.accountStatus === "Revoked";
  const isRecoveryActionAccount = isInactiveAccount || isRevokedAccount;
  const recoveryActionLabel = "Reactivate Access";
  const hasUnsavedAccessLevel = !isRecoveryActionAccount && accessLevel !== request.accessLevel;
  const unsavedHintDescribedBy = hasUnsavedAccessLevel ? unsavedHintId : undefined;
  const saveButtonLabel = isSavingAccessLevel ? "Saving..." : "Save";

  const approveHandler = useCallback(async (userRequest, selectedLevel) => {
    await approveUserAccountRequest({requestId: userRequest.id, accessLevel : selectedLevel});
    onChange();
  }, []);

  const denyHandler = useCallback(async (userRequest, selectedRevokeReason) => {
    if (!selectedRevokeReason) {
      return;
    }
    await denyUserAccountRequest({
      requestId: userRequest.id,
      denialReason: selectedRevokeReason
    });
    setRevokeReason(null);
    setRevokeReasonDropdownKey((previous) => previous + 1);
    onChange();
  }, []);

  const saveAccessLevelHandler = async () => {
    if (isSavingAccessLevel) {
      return;
    }
    setIsSavingAccessLevel(true);
    setHasSaveError(false);
    try {
      await approveUserAccountRequest({ requestId: request.id, accessLevel });
    } catch {
      setHasSaveError(true);
      setIsSavingAccessLevel(false);
      return;
    }
    setIsSavingAccessLevel(false);
    onAccessLevelSaved({ request, accessLevel });
  };

  const accessLevelChangeHandler = (event) => {
    setAccessLevel(event.detail.value.value);
    setHasSaveError(false);
    onEdit(request.id);
  };

  // Keyed on the saved level, not the request object: the list refreshes in the background
  // after any action, and resetting on every new object would wipe an in-progress selection.
  useEffect(() => {
    setAccessLevel(request.accessLevel);
  }, [request.accessLevel])

  useEffect(() => {
    if (shouldFocusAccessLevel) {
      accessLevelSelectRef.current?.focus();
    }
  }, [shouldFocusAccessLevel]);

  const openRestoreModal = useCallback(() => {
    setHasRestoreError(false);
    setIsRestoreModalOpen(true);
  }, []);

  const closeRestoreModal = useCallback(() => {
    if (isSubmittingRestore) {
      return;
    }
    setHasRestoreError(false);
    setIsRestoreModalOpen(false);
  }, [isSubmittingRestore]);

  const confirmRestoreHandler = useCallback(async () => {
    if (isSubmittingRestore) {
      return;
    }
    setIsSubmittingRestore(true);
    setHasRestoreError(false);
    try {
      await approveHandler(request, accessLevel);
      setIsRestoreModalOpen(false);
    } catch {
      setHasRestoreError(true);
    } finally {
      setIsSubmittingRestore(false);
    }
  }, [accessLevel, approveHandler, isSubmittingRestore, request]);

  const displayAccessLevelTag = (level) => {
    const text = displayAccessLevelRoleName(level, request);
    if (level === ACCESS_LEVEL_ADMIN) {
      return <span className='bg-accent-warm text-white p-1 uppercase'>{text}</span>;
    }
    if (level === ACCESS_LEVEL_USER) {
      return <span className='bg-accent-cool text-white p-1 uppercase'>{text}</span>;
    }
    return <span className='bg-accent-warm text-white p-1 uppercase'>{text}</span>;
  }

  return (
    <UserRequestContainer key={`request-${request.id}`}>
      <div className='col-start-1 col-span-4 border-0 border-r-2 border-r-gray-lighter'>
        <div className='pb-2'><span className="font-bold">Name:</span> {request.name}</div>
        <div className='pb-2'><span className="font-bold">Email:</span> {request.userName}</div>
        <div className='pb-2'><span className="font-bold">Organization:</span> {request.organizationName}</div>
        <div className='pb-2'><span className="font-bold">Requested on:</span> {formatShortDisplayDate(request.createdDate)}</div>
        <div className='pb-2'><span className="font-bold">Account status:</span> {capitalize(request.accountStatus || "unknown")}</div>
        <div className='text-sm pb-2'>{displayAccessLevelTag(request.accessLevel)}</div>
        <div className='text-sm pb-2'><span className="font-bold bg-purple-500">Approved by:</span> {request.decisionBy}</div>
      </div>
      <div className="col-span-4 [&>label]:m-0 pr-4 pl-4">
        <Dropdown
          ref={accessLevelSelectRef}
          id={`access-level-${request.id}`}
          optionRenderer={(o) => o.label}
          options={accessLevelOptions}
          comparator={(option, selected) => option?.value === selected}
          value={accessLevel}
          onChange={accessLevelChangeHandler}
          aria-label="Access level"
          aria-describedby={[accessLevelHelpId, unsavedHintDescribedBy].filter(Boolean).join(' ')}
          className='mb-4'
        >
          Change access level:
        </Dropdown>
        <p id={accessLevelHelpId} className="mb-3 text-sm text-base-dark">
          Select an access level to apply before saving or restoring account access.
        </p>
        {/* The live region must already be in the DOM when the hint is inserted to be announced. */}
        <div aria-live="polite">
          {hasUnsavedAccessLevel ? (
            <p id={unsavedHintId} className="mb-3 text-sm font-bold text-error-dark">
              Unsaved change. Select Save to apply.
            </p>
          ) : null}
        </div>
      </div>
      <div className="col-span-4 [&>label]:m-0 pl-4">
        {!isRecoveryActionAccount ? (
          <>
            <Dropdown
              key={`revoke-reason-${request.id}-${revokeReasonDropdownKey}`}
              id={`revoke-reason-${request.id}`}
              optionRenderer={(option) => option.label}
              options={denialReasonOptions}
              comparator={(option, selected) => option?.value === selected}
              value={revokeReason}
              onChange={(event) => setRevokeReason(event.detail.value.value)}
              className='mb-3'
            >
              Revoke reason:
            </Dropdown>
            <p className="mb-3 text-sm text-base-dark">
              Select a revoke reason to enable Revoke Access.
            </p>
            {revokeReason ? (
              <p className="mb-3 text-sm text-base-dark">
                {denialReasonOptions.find((option) => option.value === revokeReason)?.description}
              </p>
            ) : null}
          </>
        ) : null}
        {isRecoveryActionAccount ? (
          <p id={actionHelpId} className="mb-3 text-sm text-base-dark">
            {isRevokedAccount
              ? "This account is currently revoked. Use Reactivate Access to restore account login."
              : "This account is currently inactive. Use Reactivate Access to restore account login."}
          </p>
        ) : null}
        <ButtonGroup className='flex mt-3'>
          {!isRecoveryActionAccount ? (
            <ButtonGroupItem className='flex-1'>
              <Button
                type="button"
                className='w-full h-10 whitespace-nowrap'
                outline
                onClick={() => denyHandler(request, revokeReason)}
                disabled={!revokeReason}
              >
                Revoke Access
              </Button>
            </ButtonGroupItem>
          ) : null}
          <ButtonGroupItem className='flex-1'>
            <Button
              type="button"
              className='w-full h-10 whitespace-nowrap'
              aria-describedby={isRecoveryActionAccount ? actionHelpId : unsavedHintDescribedBy}
              aria-haspopup={isRecoveryActionAccount ? "dialog" : undefined}
              aria-controls={isRecoveryActionAccount ? restoreModalId : undefined}
              aria-expanded={isRecoveryActionAccount ? isRestoreModalOpen : undefined}
              onClick={isRecoveryActionAccount ? openRestoreModal : saveAccessLevelHandler}
              disabled={isRecoveryActionAccount ? !accessLevel : !hasUnsavedAccessLevel}
              aria-disabled={isSavingAccessLevel || undefined}
            >
              {isRecoveryActionAccount ? recoveryActionLabel : saveButtonLabel}
            </Button>
          </ButtonGroupItem>
        </ButtonGroup>
        {hasSaveError ? (
          <Alert type="error" slim className="mt-3">
            Access level not saved. Please try again in a few moments.
          </Alert>
        ) : null}
        {recentChange ? (
          <AccountChangeNotice
            className="mt-3"
            onUndo={() => onUndoAccessLevel(request)}
            undoContext={`access level change for ${request.name || request.userName}`}
          >
            {`Access level saved: ${displayAccessLevelRoleName(recentChange.accessLevel, request)}.`}
          </AccountChangeNotice>
        ) : null}
      </div>
      {isRecoveryActionAccount ? (
        <AppModal
          id={restoreModalId}
          isOpen={isRestoreModalOpen}
          onStateChanged={setIsRestoreModalOpen}
          title="Confirm account reactivation"
          body={(
            <>
              {`This will restore login access for ${request.userName}.`}
              {hasRestoreError ? (
                <Alert type="error" slim className="mt-3">
                  Account not reactivated. Please try again in a few moments.
                </Alert>
              ) : null}
            </>
          )}
          primaryActionText={isSubmittingRestore ? "Saving..." : recoveryActionLabel}
          onPrimaryAction={confirmRestoreHandler}
          secondaryActionText="Cancel"
          onSecondaryAction={closeRestoreModal}
          forceAction={isSubmittingRestore}
        />
      ) : null}
    </UserRequestContainer>
  );
}

ApprovedUserRequest.propTypes = {
  request: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onAccessLevelSaved: PropTypes.func.isRequired,
  onUndoAccessLevel: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  recentChange: PropTypes.shape({
    previousAccessLevel: PropTypes.string,
    accessLevel: PropTypes.string.isRequired,
  }),
  shouldFocusAccessLevel: PropTypes.bool,
  accessLevelOptions: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string.isRequired, value: PropTypes.string.isRequired })
  ).isRequired,
};
